/**
 * Small product-page interactions: copying a discount code, and adding a
 * cross-sell lamp to the cart without leaving the page.
 *
 * Both are delegated from document level rather than per-element listeners,
 * because Dawn re-renders sections of the product page on variant change and
 * per-element bindings would be lost.
 */

const COPY_RESET_MS = 2000;

/**
 * Copy a discount code to the clipboard and confirm it in place.
 * @param {HTMLButtonElement} button - The clicked code button.
 */
async function copyOfferCode(button) {
  const code = button.dataset.offerCode;
  const action = button.querySelector('.ovarana-offers__code-action');
  if (!code || !action) return;

  try {
    await navigator.clipboard.writeText(code);
  } catch (error) {
    // Clipboard access is refused in some embedded and insecure contexts.
    // Select the text instead so the visitor can copy it by hand rather than
    // being left with a button that silently does nothing.
    const range = document.createRange();
    range.selectNodeContents(button.querySelector('.ovarana-offers__code-value'));
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    return;
  }

  const original = action.textContent;
  action.textContent = 'Copied';
  button.setAttribute('data-copied', '');

  setTimeout(() => {
    action.textContent = original;
    button.removeAttribute('data-copied');
  }, COPY_RESET_MS);
}

/**
 * Add a cross-sell variant to the cart and refresh Dawn's cart UI.
 * @param {HTMLButtonElement} button - The clicked add button.
 */
async function addCrossSell(button) {
  const variantId = button.dataset.variantId;
  if (!variantId || button.disabled) return;

  const label = button.querySelector('[data-cross-sell-label]');
  const original = label?.textContent;

  button.disabled = true;
  if (label) label.textContent = 'Adding…';

  try {
    const body = new FormData();
    body.append('id', variantId);
    body.append('quantity', '1');

    const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body,
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);

    if (label) label.textContent = 'Added';

    // Let Dawn's cart drawer / bubble update itself rather than duplicating
    // its rendering here.
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

    setTimeout(() => {
      if (label && original) label.textContent = original;
      button.disabled = false;
    }, COPY_RESET_MS);
  } catch (error) {
    console.error('Cross-sell add to cart failed:', error);
    if (label) label.textContent = 'Try again';
    button.disabled = false;
  }
}

document.addEventListener('click', (event) => {
  const codeButton = event.target.closest('[data-offer-code]');
  if (codeButton) {
    event.preventDefault();
    copyOfferCode(codeButton);
    return;
  }

  const crossSellButton = event.target.closest('[data-cross-sell-add]');
  if (crossSellButton) {
    event.preventDefault();
    addCrossSell(crossSellButton);
  }
});
